import { useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { ContactForm } from "@/components/sections/ContactForm";
import { RelatedLinksSection } from "@/components/sections/RelatedLinksSection";
import {
  ArrowRight,
  Check,
  Users,
  Settings,
  Rocket,
  Clock,
  ShieldCheck,
  GraduationCap,
  Briefcase,
  Building2,
  TrendingUp,
  Truck,
  Store,
  Plus,
  Minus,
  Phone,
  Quote,
  Sparkles,
  UserCheck,
  Handshake,
  CalendarRange,
} from "lucide-react";
import hero from "@/assets/hero-inhouse-dark.webp";
import faqImage from "@/assets/faq-inhouse.webp";
import teamImage from "@/assets/inhouse.webp";
import storyInterim from "@/assets/inhouse-1.webp";
import storySolution from "@/assets/inhouse-2.webp";
import storyFocus from "@/assets/inhouse-3.webp";
import { StoryAlternating } from "@/components/sections/StoryAlternating";

const trustItems = [
  "Senior ekonomikompetens på plats",
  "Snabb start — ofta inom 1–2 veckor",
  "Flexibel period & omfattning",
  "Sömlös överlämning när uppdraget slutar",
];

const included = [
  {
    icon: UserCheck,
    title: "Erfaren ekonom på plats hos er",
    body: "Ni får en senior ekonom, controller eller redovisningsansvarig som kliver in i er verksamhet — från redovisningskonsult till CFO-nivå beroende på behovet.",
  },
  {
    icon: Settings,
    title: "Hela ekonomiavdelningen — eller avgränsade uppdrag",
    body: "Vi tar övergripande ansvar för bolagets ekonomi eller löser ett konkret uppdrag: månadsbokslut, systembyte, processkartläggning eller omstrukturering.",
  },
  {
    icon: Clock,
    title: "Snabb mobilisering när tiden är knapp",
    body: "När er ordinarie ekonom slutar, är sjukskriven eller ni växer snabbare än rekryteringen hinner med — vi är på plats inom dagar, inte månader.",
  },
  {
    icon: GraduationCap,
    title: "Stöd vid utbildning & onboarding",
    body: "Behöver er nyrekryterade ekonom upplärning eller mentorskap? Vår inhouse-medarbetare delar kunskap och säkerställer en trygg överlämning.",
  },
  {
    icon: Rocket,
    title: "Förstärkning under tillväxt & expansion",
    body: "Snabb expansion ställer nya krav på ekonomifunktionen. Vi förstärker tillfälligt så att processer, system och rapportering håller jämna steg med tillväxten.",
  },
  {
    icon: ShieldCheck,
    title: "Kontinuitet — inga avbrott i bokföringen",
    body: "Bokslut, skatteinlämning och rapportering blir aldrig liggande. Vi säkerställer att kvalitet och deadlines hålls — även när er egen organisation är under press.",
  },
];

const steps = [
  {
    icon: Handshake,
    title: "Vi förstår ert behov",
    body: "Kostnadsfritt avstämningsmöte där vi kartlägger vad som behöver göras, vilken kompetensnivå som krävs och hur länge uppdraget förväntas pågå.",
  },
  {
    icon: UserCheck,
    title: "Vi matchar rätt person",
    body: "Vi föreslår en eller flera medarbetare med rätt kompetens, branscherfarenhet och personlighet. Ni träffar och godkänner alltid den person som ska arbeta hos er.",
  },
  {
    icon: Rocket,
    title: "Snabb start & löpande dialog",
    body: "Uppdraget startar — på plats hos er, hybrid eller helt remote. Ni har en namngiven kontaktperson hos oss som följer upp och säkerställer att leveransen håller måttet.",
  },
];

const forWhom = [
  { icon: Briefcase, label: "Vid sjukskrivning eller föräldraledighet" },
  { icon: Rocket, label: "Snabb expansion eller tillväxtfas" },
  { icon: GraduationCap, label: "Utbildning av ny ekonompersonal" },
  { icon: CalendarRange, label: "I väntan på rekrytering" },
  { icon: Settings, label: "Vid systembyte eller processarbete" },
  { icon: Building2, label: "Inför bokslut eller revision" },
];

const why = [
  {
    title: "Interimslösning — utan långa avtal",
    body: "Ni binder er inte vid en tillsvidareanställning. Vi stannar så länge ni behöver — en månad, en termin eller ett år — och avslutar lika smidigt som vi startade.",
  },
  {
    title: "Senior kompetens från dag ett",
    body: "Våra inhouse-medarbetare är erfarna ekonomer som kommer in och levererar direkt. Ingen lång upplärningstid, inga inkörningsförluster.",
  },
  {
    title: "Flexibel omfattning — efter ert behov",
    body: "Heltid, deltid eller projektbasis. På plats, hybrid eller remote. Vi anpassar omfattning, kompetens och arbetsform efter situationen — inte tvärtom.",
  },
  {
    title: "Förbättringsåtgärder på köpet",
    body: "Eftersom vi arbetar nära er ser vi var processer kan automatiseras, vilka rapporter som faktiskt används och var ni läcker tid. Förslag på förbättringar ingår alltid.",
  },
  {
    title: "Inga rekryteringsrisker",
    body: "Slipper ni felrekrytera, betala rekryteringsavgift och hantera avveckling om det inte fungerar. Med oss kan ni testa kompetens och samarbete innan ni ev. rekryterar permanent.",
  },
  {
    title: "Sömlös överlämning vid avslut",
    body: "När ni rekryterat eller behovet upphört dokumenterar vi processer, lämnar över rutiner och säkerställer att er nya medarbetare kan ta vid utan friktion.",
  },
];

const faqs = [
  {
    q: "Hur snabbt kan ni vara på plats hos oss?",
    a: "I de flesta fall kan vi starta inom 1–2 veckor från första kontakt. Vid akuta behov — exempelvis plötslig sjukskrivning eller en uppsägning — har vi i flera fall kunnat vara på plats inom några dagar. Allt beror på vilken kompetensnivå ni behöver och hur snabbt ni kan ta emot någon.",
  },
  {
    q: "Vilken typ av roller kan ni täcka som inhouse-medarbetare?",
    a: "Vi tillsätter allt från redovisningsekonom och löneadministratör till controller, redovisningsansvarig och CFO på interimsbasis. Vi har bred branscherfarenhet — bygg, transport, fastighet, handel, tjänsteföretag, IT och tillverkning — och matchar alltid kompetensnivån mot ert specifika behov.",
  },
  {
    q: "Vad kostar en inhouse-medarbetare?",
    a: "Priset beror på kompetensnivå, omfattning (heltid/deltid) och uppdragets längd. Längre uppdrag är generellt mer förmånliga per timme. Vi tar fram en konkret offert efter ett kostnadsfritt avstämningsmöte där vi förstår behovet på djupet — så att ni vet exakt vad det kostar och vad ni får.",
  },
  {
    q: "Måste vår inhouse-medarbetare sitta hos oss på kontoret?",
    a: "Nej. Vi anpassar arbetsformen efter ert behov — på plats, hybrid eller helt remote. Många uppdrag fungerar utmärkt med en kombination, exempelvis 2 dagar på er plats och resterande tid från vårt kontor i Umeå eller Stockholm. Vi diskuterar det upplägg som passar er bäst.",
  },
  {
    q: "Vad händer om personkemin inte stämmer?",
    a: "Vi tar det på största allvar. Ni träffar alltid kandidaten innan uppdraget startar, och om det av någon anledning inte fungerar byter vi snabbt ut personen — utan extra kostnad. Vår framgång bygger på att ni är nöjda hela vägen.",
  },
  {
    q: "Kan vi anställa er inhouse-medarbetare permanent senare?",
    a: "Ja, det går utmärkt och händer regelbundet. Vi har upplägg där en del av interimsarvodet räknas av om ni väljer att rekrytera personen. Vi vill att ni ska få rätt person på lång sikt — vår roll är att lösa er situation, inte att binda er till oss.",
  },
];

const Inhouse = () => {
  const [openFaq, setOpenFaq] = useState<number>(0);

  return (
    <>
      <SEO
        title="Inhouse-medarbetare & interim ekonomi — Snabb förstärkning | Cloud Redovisning"
        description="Inhouse-medarbetare på plats hos ert företag — vid tillväxt, sjukskrivning, systembyte eller i väntan på rekrytering. Senior ekonomikompetens med start inom 1–2 veckor. Boka avstämning."
      />

      {/* ───────── HERO (dark) ───────── */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden bg-black text-background">
        <div className="absolute inset-0 -z-10">
          <img
            src={hero}
            alt=""
            className="h-full w-full object-cover opacity-40 animate-slow-zoom"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/90 to-foreground/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-transparent to-foreground/60" />
        </div>
        <div className="absolute -top-32 -right-32 -z-10 h-[480px] w-[480px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-32 -z-10 h-[420px] w-[420px] rounded-full bg-primary-glow/10 blur-3xl" />

        <div className="container-page">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-primary-glow animate-fade-up backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" /> Tjänst —
                Inhouse-medarbetare
              </div>
              <h1 className="display-xl mt-8 text-background animate-fade-up delay-100 text-balance">
                Senior ekonomikompetens —{" "}
                <span className="italic text-primary-glow">
                  på plats hos er, när det krävs
                </span>
                .
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-[1.7] font-light text-background/75 animate-fade-up delay-200">
                Ibland behövs en tillfällig lösning när företaget vill förstärka
                sin ekonomiska kompetens. Vi hjälper företag i Umeå, Stockholm
                och hela Sverige med inhouse ekonomi, interim ekonomi och snabb
                resursförstärkning från dag ett.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 animate-fade-up delay-300">
                <Link
                  to="/kontakt-umea"
                  className="inline-flex items-center gap-3 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-background hover:text-foreground shadow-elevated"
                >
                  Boka avstämningsmöte <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#sa-fungerar-det"
                  className="inline-flex items-center gap-3 rounded-full border border-background/30 bg-background/5 backdrop-blur px-7 py-3.5 text-sm font-medium text-background transition-all hover:bg-background hover:text-foreground"
                >
                  Så fungerar det
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 animate-fade-in delay-200">
              <div className="relative">
                <div className="relative aspect-[4/5] overflow-hidden rounded-3xl shadow-elevated ring-1 ring-background/10">
                  <img
                    src={hero}
                    alt="Inhouse-medarbetare på plats hos kund"
                    className="h-full w-full object-cover animate-slow-zoom"
                    fetchpriority="high"
                  />
                </div>

                <div className="absolute -left-4 md:-left-10 top-10 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[210px]">
                  <div className="font-display text-3xl text-primary-glow">
                    1–2 v
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    Tid till start för de flesta uppdrag
                  </div>
                </div>
                <div className="absolute -right-4 md:-right-8 bottom-12 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[210px]">
                  <div className="font-display text-3xl text-primary-glow">
                    CFO → Eko
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    Hela kompetensspannet täcks
                  </div>
                </div>
                <div className="absolute -right-2 md:-right-4 -top-4 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[190px]">
                  <div className="font-display text-2xl text-primary-glow">
                    Flexibelt
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    På plats, hybrid eller remote
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── TRUST BAR ───────── */}
      <section className="bg-foreground text-background py-6">
        <div className="container-page">
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
            {trustItems.map((t) => (
              <div
                key={t}
                className="inline-flex items-center gap-2 text-sm text-background/75"
              >
                <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── WHAT'S INCLUDED ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow">— Vad ni får</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              En interimslösning som{" "}
              <span className="italic text-primary">
                löser situationen — utan att binda er
              </span>
              .
            </h2>
            <p className="lead mt-6">
              Vi blir er ekonomiavdelning på plats. Vi stannar så länge ni
              önskar och tar både avgränsade uppdrag och övergripande ansvar för
              bolagets ekonomi.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {included.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="group rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/30 hover:shadow-elevated hover:-translate-y-1"
                >
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                    <Icon className="h-6 w-6 text-primary" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-medium text-foreground font-display mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-muted leading-relaxed">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── STORY — narrative pillars from original site ───────── */}
      <StoryAlternating
        background="elevated"
        sectionEyebrow="Så fungerar inhouse hos er"
        sectionTitle={
          <>
            En interimslösning som{" "}
            <span className="italic text-primary">möter er där ni är</span>.
          </>
        }
        sectionLead="Vi kliver in när det behövs, anpassar omfattningen efter er situation — och säkerställer att ni kan fokusera på det ni är bäst på."
        items={[
          {
            eyebrow: "01 — Interim",
            title: "Interimslösning för ditt företag",
            body: "Vi erbjuder en interimslösning genom en inhouse-medarbetare som tillfälligt löser er situation. Det kan vara vid snabb expansion, i utbildningssituationer eller i väntan på en långsiktig lösning. Ni binder er inte vid en tillsvidareanställning — vi stannar precis så länge ni behöver.",
            image: storyInterim,
          },
          {
            eyebrow: "02 — Lösning",
            title: "Vilken lösning behöver ni?",
            body: "Utifrån ert önskemål erbjuder vi alla typer av kvalificerade ekonomiuppdrag — vi blir som er ekonomiavdelning på plats. Som inhouse-medarbetare stannar vi under den period ni önskar och kan både ansvara för ett mindre, avgränsat arbete eller ha ett mer övergripande ansvar över ert företags ekonomi.",
            image: storySolution,
          },
          {
            eyebrow: "03 — Fokus",
            title: "Fokusera på kärnverksamheten",
            body: "I och med att vi arbetar nära er ser vi också var processer kan förbättras. Vi föreslår löpande förbättringsåtgärder inom ekonomi för att utveckla ert företag. På så sätt kan ni fokusera på kärnverksamheten — och uppnå högre effektivitet och lönsamhet utan att tappa kontrollen över ekonomin.",
            image: storyFocus,
          },
        ]}
      />

      {/* ───────── HOW IT WORKS ───────── */}
      <section
        id="sa-fungerar-det"
        className="py-20 md:py-32 bg-background-deep"
      >
        <div className="container-page">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="eyebrow">— Så fungerar det</div>
              <h2 className="display-md mt-6 text-foreground text-balance">
                Tre steg{" "}
                <span className="italic text-primary">
                  från behov till på plats
                </span>
                .
              </h2>
              <p className="lead mt-6">
                En enkel och snabb process där ni alltid behåller kontrollen —
                vi föreslår, ni godkänner, vi levererar.
              </p>

              <div className="mt-10 relative h-[320px] overflow-hidden rounded-3xl shadow-soft ring-1 ring-border">
                <img
                  src={teamImage}
                  alt="Cloud Redovisning som inhouse-medarbetare hos kund"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>

              <Link
                to="/kontakt-umea"
                className="inline-flex items-center gap-3 mt-10 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-foreground"
              >
                Starta dialog <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="space-y-5">
                {steps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={s.title}
                      className="group relative rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/30 hover:shadow-soft"
                    >
                      <div className="flex items-start gap-6">
                        <div className="relative flex-shrink-0">
                          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center text-primary-foreground shadow-soft">
                            <Icon className="h-7 w-7" strokeWidth={1.75} />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-foreground font-display">
                            {s.title}
                          </h3>
                          <p className="mt-3 text-sm text-foreground-muted leading-relaxed">
                            {s.body}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── FOR WHOM ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow">— Vi rycker in</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              När en interimslösning{" "}
              <span className="italic text-primary">är rätt val</span>.
            </h2>
            <p className="lead mt-6">
              Det finns många situationer där en inhouse-medarbetare löser
              problemet snabbare, smidigare och mer kostnadseffektivt än en
              rekrytering.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {forWhom.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className="flex items-center gap-4 rounded-2xl border border-border bg-card px-6 py-5 transition-all hover:border-primary/40 hover:shadow-soft"
                >
                  <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── WHY CLOUD ───────── */}
      <section className="py-20 md:py-32 bg-background-deep">
        <div className="container-page">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow">— Varför Cloud Redovisning</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              Inhouse — utan{" "}
              <span className="italic text-primary">
                risken med en felrekrytering
              </span>
              .
            </h2>
            <p className="lead mt-6">
              Ni får senior kompetens, full flexibilitet och förbättringsförslag
              på köpet — utan långa avtal eller rekryteringsrisker.
            </p>
          </div>

          <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
            {why.map((w) => (
              <div key={w.title} className="flex gap-5">
                <div className="flex-shrink-0 mt-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground font-display">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground-muted leading-relaxed">
                    {w.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── TESTIMONIAL ───────── */}
      <section className="py-20 md:py-32 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-10">
          <div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-primary blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-primary-glow blur-3xl" />
        </div>
        <div className="container-page relative">
          <div className="max-w-3xl mx-auto text-center">
            <Quote
              className="h-12 w-12 text-primary mx-auto mb-8"
              strokeWidth={1.5}
            />
            <blockquote className="font-display text-2xl md:text-3xl leading-relaxed text-background/90 italic text-balance">
              När vår ekonomichef sa upp sig stod vi plötsligt utan ledning över
              en kritisk funktion. Cloud Redovisning var på plats inom en vecka
              och höll i taktpinnen tills vi rekryterat — och föreslog dessutom
              processförbättringar som vi haft glädje av långt efter att
              uppdraget tog slut.
            </blockquote>
            <div className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-background/60">
              <span className="h-px w-8 bg-primary" />
              Nöjd kund — Tillväxtbolag i Stockholm
            </div>
          </div>
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="grid gap-12 lg:gap-16 lg:grid-cols-12 items-center">
            <div className="lg:col-span-4">
              <div className="eyebrow">— Vanliga frågor</div>
              <h2 className="display-md mt-6 text-foreground text-balance">
                Frågor om{" "}
                <span className="italic text-primary">inhouse-medarbetare</span>
                .
              </h2>
              <p className="lead mt-6">
                Det vi hör mest från företag som överväger en interimslösning.
              </p>
              <div className="mt-10 relative h-[320px] overflow-hidden rounded-3xl shadow-soft">
                <img
                  src={faqImage}
                  alt="Vanliga frågor om interim ekonomi"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 ring-1 ring-foreground/5 rounded-3xl" />
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="space-y-3">
                {faqs.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div
                      key={f.q}
                      className={`rounded-2xl border transition-all duration-300 ${
                        open
                          ? "border-primary/30 bg-card shadow-soft"
                          : "border-border bg-card hover:border-border-strong"
                      }`}
                    >
                      <button
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        aria-expanded={open}
                        className="w-full flex items-center justify-between gap-6 text-left p-6 md:p-7"
                      >
                        <span className="font-display text-[1.5rem] tracking-[0.005em] text-foreground">
                          {f.q}
                        </span>
                        <span
                          className={`h-9 w-9 flex-shrink-0 rounded-full border flex items-center justify-center transition-all ${
                            open
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border text-foreground"
                          }`}
                        >
                          {open ? (
                            <Minus className="h-4 w-4" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </span>
                      </button>
                      <div hidden={!open} className="px-6 md:px-7 pb-7 -mt-2 text-sm text-foreground-muted leading-relaxed animate-fade-in">
                          {f.a}
                        </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-accent p-10 md:p-16 text-primary-foreground shadow-elevated">
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary-foreground/10 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />
            <div className="relative grid gap-10 md:grid-cols-12 items-center">
              <div className="md:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-primary-foreground/70">
                  — Nästa steg
                </div>
                <h2 className="display-md mt-6 text-primary-foreground text-balance">
                  Behöver ni en ekonomisk specialist på plats{" "}
                  <span className="italic">redan nästa månad?</span>
                </h2>
                <p className="mt-6 max-w-xl text-primary-foreground/85 leading-relaxed">
                  Berätta om er situation — vi återkommer snabbt med ett konkret
                  förslag på upplägg, kompetens och tidsplan.
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
                <Link
                  to="/kontakt-umea"
                  className="inline-flex items-center gap-3 rounded-full bg-background px-7 py-3.5 text-sm font-medium text-foreground transition-all hover:bg-foreground hover:text-background"
                >
                  Boka avstämning <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="tel:0703036345"
                  className="inline-flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-primary-foreground"
                >
                  <Phone className="h-3.5 w-3.5" /> 070-303 63 45
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedLinksSection
        title="Komplettera interimslösningen med rätt specialiststöd."
        description="Många uppdrag börjar med en tillfällig förstärkning och växer sedan vidare till bokföring, lön eller strategisk rådgivning."
        links={[
          {
            to: "/bokforing",
            title: "Bokföring",
            body: "När ni vill kombinera intern ekonomi med extern kapacitet i en digital process.",
          },
          {
            to: "/loneadministration",
            title: "Löneadministration",
            body: "När ni behöver säkra lönehanteringen parallellt med ett interim- eller inhouseuppdrag.",
          },
          {
            to: "/radgivning",
            title: "Ekonomisk rådgivning",
            body: "För företag som vill använda interimstödet till långsiktig förbättring och styrning.",
          },
          {
            to: "/kontakt",
            title: "Kontakta Cloud",
            body: "Boka ett avstämningsmöte med Umeå eller Stockholm och få ett konkret förslag.",
          },
        ]}
      />

      {/* ───────── CONTACT FORM ───────── */}
      <ContactForm
        title="Kontakta oss för kostnadsfri rådgivning"
        subtitle="Berätta om er situation så återkommer vi snabbt med ett konkret förslag."
      />
    </>
  );
};

export default Inhouse;
